import { Component, Inject, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { CommonModule } from "@angular/common";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatDialogModule } from "@angular/material/dialog";
import { MatOptionModule } from "@angular/material/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { PhoneTypeService } from "app/modules/components/common/phone-type/phone-type.service.service";
import PhoneTypeAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/common/masters/phone-type/phone-type.entity";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";
import { HasPermissionPipe, PermissionDirective, PermissionService } from "app/core/permission";
import { HandlerMessageService } from "app/common/services/handler-error/handler-message.service";
import { COMPANY_EMBEDDED_CONTACT_DATA_PERMISSIONS } from "app/common/constants/permission";

@Component({
  selector: "app-add-business-partner-phone-modal",
  standalone: true,
  templateUrl: "./business-partner-phone.component.html",
  styleUrls: ["./business-partner-phone.component.scss"],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatDialogModule,
    MatOptionModule,
    NgxMaskDirective,
    PermissionDirective,
    HasPermissionPipe,
  ],
  providers: [provideNgxMask()],
})
export class AddBusinessPartnerPhoneComponent implements OnInit {
  createForm!: FormGroup;
  phoneMask: string = "0000000000"; // máscara por defecto

  isEditMode: boolean = false;
  phoneTypeOptions: any[] = [];

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private phoneTypeService: PhoneTypeService,
    public dialogRef: MatDialogRef<AddBusinessPartnerPhoneComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly permissionService: PermissionService,
    private readonly handlerMessage: HandlerMessageService
  ) {}

  ngOnInit(): void {
    this.isEditMode = !!this.data;
    this.loadPhoneTypeOptions();
    this.createForm = this.fb.group({
      _id: [this.data?._id],
      prefix: [
        this.data?.prefix || "",
        [
          Validators.required,
          Validators.pattern(/^\+?[0-9]+$/) 
        ],
      ],
      phone: [
        this.data?.phone || "",
        [
          Validators.required,
          Validators.pattern(/^[0-9]{6,15}$/), // 6 a 15 dígitos
        ],
      ],
      typeId: [
        this.data?.type?._id || this.data?.typeId || "",
        Validators.required,
      ],
      isPrincipal: [this.data?.isPrincipal || false],
    });

    this.createForm.get("prefix")?.valueChanges.subscribe((value) => {
      if (!value) return;
      let normalized = value.startsWith("+") ? value : `+${value}`;
      if (normalized !== value) {
        this.createForm
          .get("prefix")
          ?.setValue(normalized, { emitEvent: false });
      }

      // Actualiza la máscara según el prefijo
      this.phoneMask = this.getPhoneMask(normalized);
    });

    const initialPrefix = this.createForm.get("prefix")?.value;
    if (initialPrefix) {
      const normalized = initialPrefix.startsWith("+")
        ? initialPrefix
        : `+${initialPrefix}`;
      this.phoneMask = this.getPhoneMask(normalized);
      this.createForm.get("prefix")?.setValue(normalized, { emitEvent: false });
    }
  }

  getPhoneMask(prefix: string): string {
    switch (prefix) {
      case "+1": // EE.UU., Canadá
        return "(000) 000-0000";
      case "+504": // Honduras
        return "0000-0000";
      case "+52": // México
        return "(00) 0000-0000";
      case "+34": // España
        return "000 00 00 00";
      default:
        return "0000000000"; // genérico
    }
  }

  private loadPhoneTypeOptions(): void {
    this.phoneTypeService.findToComboBox().subscribe({
      next: (response) => {
        this.phoneTypeOptions = response.data;
      },
      error: (error) => {
        this.snackBar.open(
          error.error.message ||
            "Error al cargar las opciones de tipo de teléfono:",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  comparePhoneType(option1: PhoneTypeAttributes, option2: Object): boolean {
    return option1 === option2 || option1?._id === option2;
  }


  save(): void {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }
    this.dialogRef.close(this.createForm.value);
  }

  checkIfHasPermission(permission: string, errorMessage?: string): boolean {
    const hasPermission = this.permissionService.has(permission);

    if (!hasPermission && errorMessage) {
      this.handlerMessage.error({ message: errorMessage });
    }

    return hasPermission;
  }
}
